import { Logger } from '@capsicum/common';
import { Badge, DrawerWrapper, ModalService, useRouter, useRTL } from '@capsicum/common-web';
import { PartnerFilterIcon } from '@capsicum/common-web/icons';
import { useProductsLobby } from '@capsicum/features/fanshop/shared';
import { usePartnerFiltered } from '@capsicum/socios-core';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { useFetchNextProducts, useRewardsCategoryTutorial, useSetSelectedChipsIdsToURL } from '../../hooks';
import { ProductsLobbySection } from '../../ui';
import { PartnerFilter } from '../../ui/drawers/partner-filter/PartnerFilter';

type ProductsLobbyParams = {
	params: {
		id: string;
	};
};

export const ProductsLobby = ({ params }: ProductsLobbyParams) => {
	const parentCategoryId = params.id;
	const listEndRef = useRef<HTMLDivElement>(null);
	const { push, searchParams } = useRouter();
	const { breakpoints } = useTheme();
	const subcategoryIds = searchParams?.get('selected')?.split(',');
	const { selectedPartners } = usePartnerFiltered();
	const { isRTL } = useRTL();
	const [drawerState, setDrawerState] = useState(false);

	const {
		categoryTitle,
		chipCategories,
		chosenSubcategoryIds,
		fetchNextPage,
		hasNextPage,
		isCategoriesLoading,
		isFetched,
		isFetching,
		isFetchingNextPage,
		loadingProducts,
		products,
		setChosenSubcategoryIds
	} = useProductsLobby({
		parentCategoryId,
		subcategoryIds
	});

	const [chipsReady, setChipsReady] = useState(false);

	useEffect(() => {
		if ((chipCategories.length <= 1 || !chosenSubcategoryIds.length) && !isCategoriesLoading && !chipsReady) {
			chipsSelected(['all']);
			setChipsReady(true);
		}
	}, [isCategoriesLoading, chipCategories]);

	const { fakeTutorialCard } = useRewardsCategoryTutorial(loadingProducts, chipCategories);

	const handleFetchNextPage = useCallback(async () => {
		if (hasNextPage && !isFetchingNextPage) {
			try {
				await fetchNextPage();
			} catch (error) {
				Logger.error('Failed to load more items:', { error });
			}
		}
	}, [hasNextPage, fetchNextPage, isFetchingNextPage]);

	useFetchNextProducts({ fetchNextPage: handleFetchNextPage, hasNextPage, isFetching, listEndRef });

	const { chipsSelected } = useSetSelectedChipsIdsToURL({ chosenSubcategoryIds, setChosenSubcategoryIds });

	const navigateToProduct = useCallback(
		(productId?: string) => push(`/fan-shop/rewards/products/${productId}`),
		[push]
	);

	const loadPartnerFilter = useCallback(
		(showDrawer: boolean) => {
			if (window.innerWidth < breakpoints.points.mobile.maxScreenWidth) {
				ModalService.addModal({
					detached: false,
					fullHeight: true,
					renderComponent: ({ close }) => <PartnerFilter closeModal={close} />
				});
			} else {
				setDrawerState(showDrawer);
			}
		},
		[setDrawerState, breakpoints]
	);

	return (
		<S.ProductsLobbyWrapper>
			<S.TitleWrapper>
				<S.Title data-testid="product-lobby-category-title">{categoryTitle}</S.Title>
				<Badge
					enabled={!!selectedPartners?.length}
					icon={PartnerFilterIcon}
					testID="product-lobby-partner-filter-button"
					variant="green"
					onClick={() => loadPartnerFilter(true)}
				/>
			</S.TitleWrapper>
			<ProductsLobbySection
				chipCategories={chipCategories}
				chipsSelected={chipsSelected}
				isFetched={isFetched}
				isFetchingNextPage={isFetchingNextPage}
				listEndRef={listEndRef}
				loadingProducts={loadingProducts}
				mockTutorialCard={fakeTutorialCard}
				products={products}
				onProductClick={navigateToProduct}
			/>
			<DrawerWrapper
				drawerPosition="right"
				isRTL={isRTL}
				showDrawer={drawerState}
				testID="productLobby-wrapper"
				onCloseDrawer={() => {
					loadPartnerFilter(false);
				}}
			>
				<PartnerFilter
					closeModal={() => {
						loadPartnerFilter(false);
					}}
				/>
			</DrawerWrapper>
		</S.ProductsLobbyWrapper>
	);
};

namespace S {
	export const ProductsLobbyWrapper = styled.div`
		width: 100%;

		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			padding: ${({ theme }) => theme.spacings.small};
		}

		${({ theme }) => theme.breakpoints.queries.only.tabletSmallDesktop} {
			padding: ${({ theme }) => theme.spacings.medium};
		}

		${({ theme }) => theme.breakpoints.queries.only.largeDesktop} {
			padding: ${({ theme }) => theme.spacings.xxlarge};
		}
	`;

	export const TitleWrapper = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: flex-start;
	`;

	export const Title = styled.p`
		${({ theme }) => theme.fonts.headings.title5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		word-break: break-word;
		flex-shrink: 1;
	`;
}
